import React, { useCallback, useMemo, useRef } from 'react';
import { getLocale, useIntl } from 'umi';
import { Bubble } from 'tea-component';
import moment from 'moment';
import GameImage from '@home/components/GameImage';
import { openPDFPreviewDownloadPage } from '@intelligence/components/ReportCard/utils';
import { getReportResourcePath } from '@intelligence/utils/reports';
import { formatReportLike, getReportTypeConfig } from '@intelligence/_config/reportConfig';
import { getName } from '@home/utils/common';
import { splitStringByChars } from '@home/utils/utils';
import useData from '@intelligence/components/ReportCard/_hook';
import { buildDatasourceAuthor, buildReportAuthors } from '@intelligence/components/ReportCard/authors';
import type { IReportAuthorViewModel } from '@intelligence/components/ReportCard/authors';
import type { IntelligenceReportDataInfo } from '../../types';
import type { TReportConfigMap } from '@intelligence/utils/reportConfig';
import { resolveReportConfig } from '@intelligence/utils/reportConfig';
import { useOverflowTip } from '../../hooks/useOverflowTip';
import { sanitizeField, isDatasourceReport, getDatasourceDisplay } from '../../utils';
import ReportAuthorPreview from '../ReportAuthorPreview';

import './index.scss';

interface ILatestReportCardProps {
  report: IntelligenceReportDataInfo;
  reportSourceConfig: TReportConfigMap;
  isEn: boolean;
  onLikeChange?: (resourceId: string, likeCounts?: number) => void;
}

const LatestReportCard: React.FC<ILatestReportCardProps> = ({
  report,
  reportSourceConfig,
  isEn,
  onLikeChange,
}) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const lang = isEn ? 'en' : 'cn';

  const reportTypeConfig = useMemo(
    () => getReportTypeConfig(report.system),
    [report.system],
  );

  const isNew = !!report.create_time
    && moment(report.create_time).isAfter(moment().subtract(1, 'month'));


  const title = sanitizeField(getName(isEn, report.title_en, report.title));
  const summary = sanitizeField(getName(isEn, report.introduction_en, report.introduction));
  const rawTags: string[] = (isEn ? report.tag_en : report.tag) || [];
  const tags: string[] = rawTags.flatMap(tag => splitStringByChars(tag)).filter(Boolean);
  const dateStr = report.create_time
    ? moment(report.create_time).format('YYYY-MM-DD')
    : '';

  const { display_source: displaySource } = resolveReportConfig(report, reportSourceConfig);
  const sourceConfig = useMemo(
    () => getDatasourceDisplay(displaySource),
    [displaySource],
  );

  const authors: IReportAuthorViewModel[] = useMemo(() => {
    if (isDatasourceReport(report.system)) {
      return [buildDatasourceAuthor(sourceConfig?.name || '', sourceConfig?.img || '')];
    }
    return buildReportAuthors(report, { textOrder: 'en-cn' });
  }, [report, sourceConfig]);
  const authorLang = isEn ? 0 : 1;

  const { tip: titleTip, handleMouseEnter: handleTitleMouseEnter } = useOverflowTip(title);
  const { tip: summaryTip, handleMouseEnter: handleSummaryMouseEnter } = useOverflowTip(summary);

  const {
    onLikeClick,
    likeBubble,
    likeCnt,
    setLikeCnt,
  } = useData({
    data: { ...report, title, report_like: report.report_like },
    systemId: reportTypeConfig.systemIdCur || report.system,
    lang: isEn ? 0 : 1,
    type: report.system,
  });

  const likeRequestDataRef = useRef<number[]>([]);

  const handleCardClick = useCallback((): void => {
    if (!report?.resource_id) return;

    openPDFPreviewDownloadPage('/pdfPreviewDownloadLikeShare', {
      id: report.id,
      systemId: reportTypeConfig.systemIdCur || report.system,
      resourceId: report.resource_id,
      resourceName: title,
      resourcePath: getReportResourcePath(locale, {
        ...report,
        lang,
      }),
      resourceLike: report.report_like,
      downloadLogKey: String(reportTypeConfig.downloadLogKeyCur || ''),
      lang,
      referer: '/intelligence/reports/reportSearch',
    });
  }, [report, reportTypeConfig, title, locale, lang]);

  const handleLikeClick = useCallback(
    async (e: React.MouseEvent): Promise<void> => {
      e.stopPropagation();
      e.preventDefault();
      if (!reportTypeConfig.likedClickable) return;

      try {
        onLikeChange?.(report.resource_id);
        setLikeCnt((prev: number) => {
          const next = prev + 1;
          likeRequestDataRef.current.push(next);
          return next;
        });

        const likeCounts = await onLikeClick(
          report.resource_id,
          reportTypeConfig.systemIdCur,
        );

        likeRequestDataRef.current.push(likeCounts);
        const latestLikeCounts = Math.max(...likeRequestDataRef.current);
        setLikeCnt(latestLikeCounts);
        onLikeChange?.(report.resource_id, latestLikeCounts);
      } catch (error) {
        console.error(error);
      }
    },
    [report.resource_id, reportTypeConfig, onLikeChange, onLikeClick, setLikeCnt],
  );

  const visibleTags = tags.filter(Boolean).slice(0, 5);
  const extraTags = tags.filter(Boolean).slice(5);

  return (
    <div className="dbi-latest-card" onClick={handleCardClick}>
      <GameImage
        className="dbi-latest-card__cover"
        src={report.cover === 'error' ? '' : report.cover}
        alt="cover"
        entityType="report"
        loading="lazy"
      />

      <div className="dbi-latest-card__content">
        <div className="dbi-latest-card__top">
          <div className="dbi-latest-card__title-row">
            <Bubble content={titleTip}>
              <div
                className="dbi-latest-card__title"
                onMouseEnter={handleTitleMouseEnter}
              >
                {title}
              </div>
            </Bubble>
            {isNew && <span className="dbi-latest-card__new-badge">NEW</span>}
          </div>

          <Bubble content={summaryTip} style={{ maxWidth: 550 }}>
            <div
              className="dbi-latest-card__summary"
              onMouseEnter={handleSummaryMouseEnter}
              dangerouslySetInnerHTML={{ __html: summary || '' }}
            />
          </Bubble>

          {visibleTags.length > 0 && (
            <div className="dbi-latest-card__tags">
              {visibleTags.map((tag, idx) => (
                <span className="dbi-latest-card__tag" key={idx}>
                  {tag}
                </span>
              ))}
              {extraTags.length > 0 && (
                <Bubble
                  style={{ maxWidth: 500 }}
                  dark
                  content={<span>{extraTags.join(', ')}</span>}
                >
                  <span className="dbi-latest-card__tag">
                    +{extraTags.length}
                  </span>
                </Bubble>
              )}
            </div>
          )}
        </div>
        <div className="dbi-latest-card__bottom">
          <div className="dbi-latest-card__author">
            <ReportAuthorPreview authors={authors} lang={authorLang} />
          </div>

          <div className="dbi-latest-card__meta">
            {dateStr && (
              <span className="dbi-latest-card__date">{dateStr}</span>
            )}
            {reportTypeConfig.showLiked && (
              <span
                className="dbi-latest-card__liked"
                onClick={(e) => void handleLikeClick(e)}
              >
                <Bubble
                  dark
                  trigger="click"
                  visible={likeBubble}
                  updateDeps={[report.report_like]}
                  content={formatMessage({ id: 'dbi_insight_report_feedback_tips' })}
                >
                  <span className="dbi-latest-card__like-icon" />
                </Bubble>
                <span>
                  {formatMessage({ id: 'dbi_reports_liked' })} {formatReportLike(likeCnt)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LatestReportCard);
